import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LessonsCarousel from "./Lessons";
import { useModules } from "../../../../queries/professoru/contents.api";
import MusicLoader from "../../../../helper/MusicLoader";
import { GETTING_STARTED_MODULE_ID, GETTING_STARTED_MODULE_TITLE } from "../lessonsData";

export default function LessonViewer({ lessons, currentModuleId, onModuleChange }) {
  const tabs = lessons ? Object.keys(lessons) : [];
  const [activeTab, setActiveTab] = useState(() => tabs[0] || "");
  const { data: modules, isPending } = useModules()

  const sortedModules = modules
    ? [...modules]
      .filter((module) => module.order !== undefined && ![GETTING_STARTED_MODULE_TITLE, "Dashboard"].includes(module.title))
      .sort((a, b) => a.order - b.order)
    : [];

  const currentModuleIndex = sortedModules.findIndex((module) => module._id === currentModuleId);
  const currentModule = currentModuleIndex >= 0 ? sortedModules[currentModuleIndex] : null;
  const prevModule = currentModuleIndex > 0 ? sortedModules[currentModuleIndex - 1] : null;
  const nextModule = currentModuleIndex >= 0 && currentModuleIndex < sortedModules.length - 1 ? sortedModules[currentModuleIndex + 1] : null;

  useEffect(() => {
    if (!lessons) return;
    const keys = Object.keys(lessons);
    if (!keys.length) return;

    if (currentModule?.title && lessons[currentModule.title]) {
      setActiveTab(currentModule.title);
      return;
    }

    setActiveTab(keys[0]);
  }, [lessons, currentModule]);

  const activeData = lessons && activeTab ? lessons[activeTab] || {
    walkthroughs: [],
    lessons: [],
    interview: []
  } : null;

  const handleModuleNavigation = (moduleId, moduleName) => {
    if (!moduleId || !moduleName) return;
    setActiveTab(moduleName);
    onModuleChange?.(moduleId, moduleName);
    // navigate(`/admin/professor-u/lessons?lesson=${moduleName}&moduleId=${moduleId}`);
  }

  const handlePrev = () => {
    if (prevModule) {
      handleModuleNavigation(prevModule._id, prevModule.title);
      return;
    }

    if (!tabs.length) return;
    const currentIndex = tabs.indexOf(activeTab);
    const prevIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
    setActiveTab(tabs[prevIndex]);
  };

  const handleNext = () => {
    if (nextModule) {
      handleModuleNavigation(nextModule._id, nextModule.title);
      return;
    }

    if (!tabs.length) return;
    const currentIndex = tabs.indexOf(activeTab);
    const nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
    setActiveTab(tabs[nextIndex]);
  };

  // Check if lessons data is empty
  const hasLessons = lessons && Object.keys(lessons).length > 0;
  const hasAnyContent = hasLessons && (
    (activeData?.walkthroughs?.length > 0) ||
    (activeData?.lessons?.length > 0) ||
    (activeData?.interview?.length > 0)
  );

  // Show loader if still loading
  if (lessons === null || (isPending && currentModuleId !== GETTING_STARTED_MODULE_ID)) {
    return (
      <div className="bg-theme_clr1 p-2 pt-4 rounded-2xl mt-4 flex justify-center items-center min-h-96">
        <MusicLoader />
      </div>
    );
  }

  // Show "No Lessons available" if no data
  const showEmptyState = !hasAnyContent;
  const isGettingStarted = (activeTab || currentModule?.title) === GETTING_STARTED_MODULE_TITLE;

  return (
    <div className="bg-theme_clr1 p-2 pt-4 rounded-2xl mt-4">

      {/* TOP BADGE */}
      <div className="flex justify-center mb-6">
        <div className="bg-theme_clr2 text-white px-16 py-1 rounded-lg font-semibold">
          {activeTab || currentModule?.title || "Lessons"}
        </div>
      </div>

      {showEmptyState && (
        <div className="flex justify-center items-center min-h-72">
          <p className="text-white text-xl font-semibold">No Lessons available</p>
        </div>
      )}

      {/* SECTION 1 */}
      <div data-tour="module-walkthroughs">
        {!isGettingStarted && activeData?.walkthroughs?.length > 0 &&
          <LessonsCarousel
            title="Module Walkthroughs and Guides"
            lessonsData={activeData.walkthroughs}
          />
        }
      </div>

      {/* SECTION 2 */}
      <div data-tour="lessons">
        {activeData?.lessons?.length > 0 &&
          <LessonsCarousel
            title="Lessons with ProfessorU"
            lessonsData={activeData.lessons}
            showIndexing={false}
            firstLessonTourEnabled={false}
          />
        }
      </div>

      {/* SECTION 3 */}
      <div data-tour="industry-talks">
        {activeData?.interview?.length > 0 &&
          <LessonsCarousel
            title="Industry Talks"
            lessonsData={activeData.interview}
          />
        }
      </div>

      {/* BOTTOM NAVIGATION */}
      <div className="flex justify-center items-center gap-16 pb-4 text-white" data-tour="module-navigation">
        <button onClick={handlePrev}>
          <ChevronLeft className="cursor-pointer" size={30} />
        </button>

        <button onClick={handleNext}>
          <ChevronRight className="cursor-pointer" size={30} />
        </button>
      </div>
    </div>
  );
}